import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeftRight,
  ArrowUpRight,
  Building2,
  ChevronsUpDown,
  CreditCard,
  FileText,
  Inbox,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Mail,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  ShieldCheck,
  Sparkles,
  Sun,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@ampm-7en-ai/ui/lib/utils';
import { Avatar } from '@ampm-7en-ai/ui/components/Avatar';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { Tooltip } from '@ampm-7en-ai/ui/components/Tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ampm-7en-ai/ui/components/DropdownMenu';
import { toClassicPath } from '@/lib/uiSwitch';
import { useAuth } from '@/context/AuthContext';
import { useAppTheme } from '@/hooks/useAppTheme';

interface NavEntry {
  label: string;
  href: string;
  icon: LucideIcon;
  relatedPaths?: string[];
}

interface NavGroup {
  id: string;
  label: string;
  items: NavEntry[];
}

const GROUPS: NavGroup[] = [
  {
    id: 'workspace',
    label: 'Workspace',
    items: [
      { label: 'Dashboard', href: '/v2/dashboard', icon: LayoutDashboard },
      { label: 'Businesses', href: '/v2/businesses', icon: Building2 },
      { label: 'Support issues', href: '/v2/support-issues', icon: LifeBuoy },
      { label: 'Contact requests', href: '/v2/contact-requests', icon: Inbox },
    ],
  },
  {
    id: 'platform',
    label: 'Platform',
    items: [
      { label: 'General settings', href: '/v2/settings/platform/general', icon: Settings },
      { label: 'Security', href: '/v2/settings/platform/security', icon: ShieldCheck },
      { label: 'AI providers', href: '/v2/settings/platform/llm-providers', icon: Sparkles },
      {
        label: 'Billing',
        href: '/v2/settings/platform/billing',
        icon: CreditCard,
        relatedPaths: ['/v2/settings/platform/subscription-plan'],
      },
      { label: 'Email templates', href: '/v2/settings/platform/customization', icon: Mail },
      { label: 'Legal articles', href: '/v2/settings/platform/legal', icon: FileText },
    ],
  },
];

const DOCS = 'https://docs.7en.ai/';

const rowClass = (active: boolean, collapsed: boolean) =>
  cn(
    'relative flex h-10 w-full items-center gap-3 rounded-ui-sm px-3 text-ui-base transition-colors duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ui-sidebar',
    collapsed && 'justify-center px-0',
    active
      ? 'bg-ui-accent/10 font-medium text-ui-accent-text'
      : 'text-ui-fg-muted hover:bg-ui-subtle hover:text-ui-fg'
  );

export interface SidebarV2Props {
  isCollapsed: boolean;
  toggleSidebar: () => void;
}

export default function SidebarV2({ isCollapsed, toggleSidebar }: SidebarV2Props) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useAppTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const id = React.useId();
  const navigationRef = React.useRef<HTMLElement>(null);
  const name = user?.name || 'User';
  const email = user?.email || '';
  const initials = name.trim().split(/\s+/).slice(0, 2).map((part) => part.charAt(0)).join('').toUpperCase() || '?';
  const collapseLabel = isCollapsed ? 'Expand sidebar' : 'Collapse sidebar';

  React.useEffect(() => {
    const navigation = navigationRef.current;
    const activeLink = navigation?.querySelector<HTMLAnchorElement>('a[aria-current="page"]');
    if (!navigation || !activeLink) return;
    const bounds = navigation.getBoundingClientRect();
    const activeBounds = activeLink.getBoundingClientRect();
    if (activeBounds.top < bounds.top) navigation.scrollTop += activeBounds.top - bounds.top;
    else if (activeBounds.bottom > bounds.bottom) navigation.scrollTop += activeBounds.bottom - bounds.bottom;
  }, [location.pathname, isCollapsed]);

  return (
    <aside
      id={id}
      aria-label="Superadmin sidebar"
      className={cn(
        'flex h-full shrink-0 flex-col border-r border-ui-border bg-ui-sidebar font-ui text-ui-fg antialiased transition-[width] duration-200 motion-reduce:transition-none',
        isCollapsed ? 'w-[72px]' : 'w-64'
      )}
    >
      <div className={cn('flex shrink-0 items-center gap-3 border-b border-ui-border px-4 py-5', isCollapsed && 'flex-col px-3 py-4')}>
        <Link
          to="/v2/dashboard"
          aria-label="7en superadmin home"
          className="min-w-0 flex-1 rounded-ui-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring"
        >
          <img
            src={isCollapsed
              ? theme === 'light' ? '/logo-icon-new.svg' : '/logo-white-icon-new.svg'
              : theme === 'light' ? '/logo-new.svg' : '/logo-white-new.svg'}
            alt=""
            className={isCollapsed ? 'h-7 w-7' : 'h-6'}
          />
          {!isCollapsed && <span className="mt-2 block text-ui-xs font-medium text-ui-fg-muted">Superadmin console</span>}
        </Link>
        <Tooltip content={collapseLabel} side={isCollapsed ? 'right' : 'bottom'}>
          <Button
            variant="ghost"
            size="icon"
            aria-label={collapseLabel}
            aria-expanded={!isCollapsed}
            aria-controls={id}
            onClick={toggleSidebar}
            className="shrink-0 rounded-ui-sm"
          >
            {isCollapsed ? <PanelLeftOpen aria-hidden /> : <PanelLeftClose aria-hidden />}
          </Button>
        </Tooltip>
      </div>

      <nav
        ref={navigationRef}
        aria-label="Main"
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-5 [scrollbar-width:thin] [scrollbar-color:hsl(var(--v2-border-strong))_transparent]"
      >
        {GROUPS.map((group, index) => (
          <section
            key={group.id}
            aria-labelledby={`${id}-${group.id}`}
            className={cn(index > 0 && 'mt-6', isCollapsed && index > 0 && 'border-t border-ui-border pt-4')}
          >
            <h2
              id={`${id}-${group.id}`}
              className={isCollapsed ? 'sr-only' : 'mb-2 px-3 text-ui-xs font-medium uppercase tracking-wider text-ui-fg-subtle'}
            >
              {group.label}
            </h2>
            <ul className="space-y-1">
              {group.items.map((entry) => {
                const active = [entry.href, ...(entry.relatedPaths ?? [])].some(
                  (pathname) => location.pathname === pathname || location.pathname.startsWith(`${pathname}/`)
                );
                const link = (
                  <Link
                    to={entry.href}
                    aria-label={entry.label}
                    aria-current={active ? 'page' : undefined}
                    className={rowClass(active, isCollapsed)}
                  >
                    {active && <span aria-hidden className="absolute left-0 h-4 w-0.5 rounded-full bg-ui-accent" />}
                    <entry.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden />
                    {!isCollapsed && <span className="truncate">{entry.label}</span>}
                  </Link>
                );
                return (
                  <li key={entry.href}>
                    {isCollapsed ? <Tooltip content={entry.label} side="right" delay={0}>{link}</Tooltip> : link}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </nav>

      <div className="shrink-0 border-t border-ui-border p-3">
        <Tooltip content="Documentation" side="right">
          <a
            href={DOCS}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Documentation (opens in a new tab)"
            className={rowClass(false, isCollapsed)}
          >
            <LifeBuoy className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden />
            {!isCollapsed && <>
              <span className="flex-1 truncate">Documentation</span>
              <ArrowUpRight className="h-3.5 w-3.5 text-ui-fg-subtle" aria-hidden />
            </>}
          </a>
        </Tooltip>

        <DropdownMenu>
          <Tooltip content={`Account: ${name}`} side="right">
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Account menu for ${name}`}
                className={cn(rowClass(false, isCollapsed), 'mt-2 h-14', !isCollapsed && 'border border-ui-border bg-ui-surface')}
              >
                <Avatar initials={initials} size="sm" tone="accent" />
                {!isCollapsed && <>
                  <span className="min-w-0 flex-1 text-left">
                    <span className="block truncate text-ui-base font-medium text-ui-fg">{name}</span>
                    {email && <span className="block truncate text-ui-xs text-ui-fg-muted">{email}</span>}
                  </span>
                  <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-ui-fg-subtle" aria-hidden />
                </>}
              </button>
            </DropdownMenuTrigger>
          </Tooltip>
          <DropdownMenuContent align="start" side={isCollapsed ? 'right' : 'top'} className="w-64">
            <DropdownMenuLabel>
              <span className="block truncate">{name}</span>
              {email && <span className="mt-1 block truncate text-ui-xs font-normal text-ui-fg-muted">{email}</span>}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              icon={theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              onSelect={toggleTheme}
            >
              {theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
            </DropdownMenuItem>
            <DropdownMenuItem
              icon={<ArrowLeftRight className="h-4 w-4" />}
              onSelect={() => navigate(toClassicPath(location.pathname, location.search, location.hash))}
            >
              Switch to classic UI
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem tone="danger" icon={<LogOut className="h-4 w-4" />} onSelect={logout}>
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
